import type { HomeyAPI, FlowCardTrigger } from 'homey-api';

import { BaseApiStore, type FetchArgs } from '../BaseApiStore.ts';
import { __DEV__ } from '../../lib/__dev__.js';

export type FlowCardTriggerProps = {
  [K in keyof FlowCardTrigger as FlowCardTrigger[K] extends Function
    ? never
    : K]: FlowCardTrigger[K];
};

type FlowCardTriggersData = { [key: string]: FlowCardTrigger };
type FlowCardTriggersById = { [key: string]: FlowCardTriggerProps };

interface State {
  data: FlowCardTriggersData | null;
  byId: FlowCardTriggersById | null;
  isLoading: boolean;
  error: unknown;
}

export type FlowCardTriggersStoreState = State;

export class FlowCardTriggersStore extends BaseApiStore<State, FlowCardTrigger> {
  constructor() {
    super('flow-card-triggers');
  }

  override createInitialState() {
    return {
      data: null,
      byId: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, silent, cache, abortController }: FetchArgs) {
    if (silent === false) {
      this.set(this.createInitialState());
    }

    try {
      await this.connectManager({ api, manager: api.flow, reference: this });

      if (abortController.signal.aborted) return;

      const flowCardTriggersPromise = api.flow.getFlowCardTriggers({ $cache: cache });

      const [flowCardTriggersData] = await Promise.all([flowCardTriggersPromise]);

      if (abortController.signal.aborted) return;

      const flowCardTriggers = Object.values(flowCardTriggersData);

      const data: FlowCardTriggersData = {};
      const byId: FlowCardTriggersById = {};

      for (const flowCardTriggerInstance of flowCardTriggers) {
        const flowCardTriggerReference = { ...flowCardTriggerInstance };

        data[flowCardTriggerReference.id] = flowCardTriggerInstance;
        byId[flowCardTriggerReference.id] = flowCardTriggerReference;
      }

      this.set({
        ...this.createInitialState(),
        data: data,
        byId: byId,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) return;

      this.set({
        ...this.createInitialState(),
        isLoading: false,
        error,
      });
    }
  }

  override destroy(reason: string) {
    try {
      const api = this.getApi();
      this.disconnectManager({ api, manager: api.flow, reference: this }).catch(console.error);
    } catch (error) {}
  }

  override onAttached({ api }: { api: HomeyAPI }): void {
    api.flow.on('flowcardtrigger.create', this.handleCreate);
    api.flow.on('flowcardtrigger.update', this.handleUpdate);
    api.flow.on('flowcardtrigger.delete', this.handleDelete);
  }

  override onDetached({ api }: { api: HomeyAPI }): void {
    api.flow.off('flowcardtrigger.create', this.handleCreate);
    api.flow.off('flowcardtrigger.update', this.handleUpdate);
    api.flow.off('flowcardtrigger.delete', this.handleDelete);
  }

  getInstanceFromCache(flowCardTrigger: { id: string }) {
    return this.getCache()[flowCardTrigger.id];
  }

  getCache(): { [key: string]: FlowCardTrigger } {
    const api = this.getApi();
    const cache = api.flow.__cache['flowcardtrigger'];
    return cache;
  }

  handleCreate = (createdFlowCardTrigger: FlowCardTrigger) => {
    this.DEBUG && console.info(`create:flowcardtrigger:${createdFlowCardTrigger.id}`);
    const state = this.get();
    const flowCardTriggerInstance = this.getInstanceFromCache(createdFlowCardTrigger);

    if (flowCardTriggerInstance == null) return;

    const flowCardTriggerReference = { ...flowCardTriggerInstance };

    this.set({
      data: {
        ...state.data,
        [createdFlowCardTrigger.id]: flowCardTriggerInstance,
      },
      byId: {
        ...state.byId,
        [createdFlowCardTrigger.id]: flowCardTriggerReference,
      },
    });
  };

  handleUpdate = (updatedFlowCardTrigger: FlowCardTrigger) => {
    this.DEBUG && console.info(`update:flowcardtrigger:${updatedFlowCardTrigger.id}`);

    this.addToQueue(updatedFlowCardTrigger.id, updatedFlowCardTrigger);

    this.batch((updateQueue) => {
      const state = this.get();

      const nextById = {
        ...state.byId,
      };

      let updateCount = 0;

      for (const [id, queuedFlowCardTrigger] of Object.entries(updateQueue)) {
        const flowCardTriggerInstance = this.getInstanceFromCache(queuedFlowCardTrigger);

        if (state.data == null) {
          this.DEBUG && console.info(`update:flowcardtrigger:missing-data`);
          continue;
        }

        if (flowCardTriggerInstance == null || state.data[id] == null) {
          this.DEBUG && console.info(`update:flowcardtrigger:unknown:${id}`);
          continue;
        }

        const flowCardTriggerReference = { ...flowCardTriggerInstance };

        nextById[id] = flowCardTriggerReference;

        updateCount++;
      }

      this.DEBUG && console.info(`update:flowcardtrigger:batch:count:${updateCount}`);

      updateCount > 0 &&
        this.set({
          byId: nextById,
        });
    });
  };

  handleDelete = (deletedFlowCardTrigger: FlowCardTrigger) => {
    this.DEBUG && console.info(`delete:flowcardtrigger:${deletedFlowCardTrigger.id}`);
    const state = this.get();

    if (state.data == null) {
      __DEV__ && console.log(`delete:flowcardtrigger:missing-data`);
      return;
    }

    const flowCardTriggerInstance = state.data[deletedFlowCardTrigger.id];

    if (flowCardTriggerInstance == null) return;

    const nextData = { ...state.data };
    const nextById = { ...state.byId };

    delete nextData[flowCardTriggerInstance.id];
    delete nextById[flowCardTriggerInstance.id];

    this.set({
      data: nextData,
      byId: nextById,
    });
  };

  restore() {
    const cache = this.getCache();
    const advancedFlows = Object.values(cache ?? {});
    const data: FlowCardTriggersData = {};
    const byId: FlowCardTriggersById = {};

    for (const flowCardTriggerInstance of advancedFlows) {
      const flowCardTriggerReference = { ...flowCardTriggerInstance };
      data[flowCardTriggerReference.id] = flowCardTriggerInstance;
      byId[flowCardTriggerReference.id] = flowCardTriggerReference;
    }

    this.set({
      data: data,
      byId: byId,
    });
  }
}

export const flowCardTriggersStore = new FlowCardTriggersStore();
